/**
 * Utility functions for machine type detection and hierarchy navigation
 */

/**
 * Determines if a node is a leaf node (no children)
 * @param {Object} node - Machine node from hierarchy
 * @returns {boolean}
 */
/**
 * Determines if a node is a leaf node (no children)
 * @param {Object} node - Machine node from hierarchy
 * @returns {boolean}
 */
export const isLeafNode = (node) => {
  if (!node) return true;

  // Check for new structure (children array)
  if (node.children && node.children.length > 0) {
    return false;
  }

  // Check for legacy structure
  const hasChildren =
    (node.branches && node.branches.length > 0) ||
    (node.sections && node.sections.length > 0) ||
    (node.machines && node.machines.length > 0);

  return !hasChildren;
};

/**
 * Determines the machine type based on node properties
 * @param {Object} node - Machine node from hierarchy
 * @returns {'generation' | 'consumption' | 'building'}
 */
export const getMachineType = (node) => {
  if (!node) return "building";

  // Check explicit category first (New Structure)
  if (node.category) {
    const cat = node.category.toUpperCase();
    if (["SOLAR", "GENERATOR", "WIND"].includes(cat)) {
      return "generation";
    }
    if (["GRID"].includes(cat)) {
      return "net"; // Or building/consumption depending on usage
    }
  }

  // If node has children, it's a building/aggregation node
  if (!isLeafNode(node)) {
    // Exception: Solar roots might have children but are still generation type?
    // Using category check above handles this.
    return "building";
  }

  // Check for generation keywords in machine name
  const name = (
    node.name ||
    node.machine_name ||
    node.section_name ||
    node.branch_name ||
    node.building_name ||
    ""
  ).toLowerCase();

  const generationKeywords = [
    "solar",
    "generator",
    "gen",
    "pv",
    "wind",
    "renewable",
  ];
  const isGeneration = generationKeywords.some((keyword) =>
    name.includes(keyword),
  );

  if (isGeneration) {
    return "generation";
  }

  // Default to consumption for leaf nodes
  return "consumption";
};

/**
 * Gets direct child nodes from a parent node
 * @param {Object} node - Parent node
 * @returns {Array} Array of child nodes
 */
export const getChildNodes = (node) => {
  if (!node) return [];

  // New structure
  if (node.children && node.children.length > 0) {
    return node.children;
  }

  // Legacy structure
  if (node.branches && node.branches.length > 0) {
    return node.branches;
  }

  if (node.sections && node.sections.length > 0) {
    return node.sections;
  }

  if (node.machines && node.machines.length > 0) {
    return node.machines;
  }

  return [];
};

/**
 * Finds sibling nodes for a given node in the hierarchy
 * @param {Object} node - Current node
 * @param {Object} hierarchyData - Full hierarchy data
 * @param {Object} parentNode - Parent node (if known)
 * @returns {Array} Array of sibling nodes
 */
export const getSiblingNodes = (node, hierarchyData, parentNode = null) => {
  if (!node || !hierarchyData) return [];

  // If parent is provided, get children from parent
  if (parentNode) {
    const children = getChildNodes(parentNode);
    return children.filter((child) => child !== node);
  }

  // Otherwise, search for the node in the hierarchy and find its siblings
  const findSiblings = (currentNode, targetNode) => {
    const children = getChildNodes(currentNode);

    // Check if target is among children
    if (children.includes(targetNode)) {
      return children.filter((child) => child !== targetNode);
    }

    // Recursively search in children
    for (const child of children) {
      const result = findSiblings(child, targetNode);
      if (result.length > 0) return result;
    }

    return [];
  };

  return findSiblings(hierarchyData, node);
};

/**
 * Gets the machine name from a node
 * @param {Object} node - Machine node
 * @returns {string}
 */
export const getMachineName = (node) => {
  if (!node) return "Unknown";

  return (
    node.name ||
    node.machine_name ||
    node.section_name ||
    node.branch_name ||
    node.building_name ||
    "Unknown"
  );
};

/**
 * Determines which columns should be visible based on machine type
 * @param {string} machineType - 'generation', 'consumption', or 'building'
 * @returns {Object} Object with boolean flags for column visibility
 */
export const getColumnVisibility = (machineType) => {
  switch (machineType) {
    case "generation":
      return {
        showGenerated: true,
        showConsumed: false,
        showNet: false,
      };
    case "consumption":
      return {
        showGenerated: false,
        showConsumed: true,
        showNet: false,
      };
    case "building":
    default:
      return {
        showGenerated: true,
        showConsumed: true,
        showNet: true,
      };
  }
};

/**
 * Gets the appropriate label for the main energy card
 * @param {string} machineType - 'generation', 'consumption', or 'building'
 * @returns {string}
 */
export const getMainEnergyLabel = (machineType) => {
  switch (machineType) {
    case "generation":
      return "Generation";
    case "consumption":
      return "Consumption";
    case "building":
    default:
      return "Net Energy";
  }
};

/**
 * Flattens hierarchy to get all leaf nodes (machines)
 * @param {Object} node - Root node
 * @returns {Array} Array of all leaf nodes
 */
export const getAllLeafNodes = (node) => {
  if (!node) return [];

  if (isLeafNode(node)) {
    return [node];
  }

  const children = getChildNodes(node);
  const leafNodes = [];

  for (const child of children) {
    leafNodes.push(...getAllLeafNodes(child));
  }

  return leafNodes;
};
